import {
  Component,
  ContentChildren,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NiTableColumn } from './ni-table.models';
import { NiTableCellDirective } from './ni-table-cell.directive';

@Component({
  selector: 'NiTable, ni-table',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './ni-table.component.html',
  styleUrls: ['./ni-table.component.css'],
})
export class NiTableComponent<T = unknown> implements OnInit, OnChanges {
  @Input() columns: NiTableColumn<T>[] = [];
  @Input() data: T[] = [];
  @Input() trackByKey = 'id';
  @Input() emptyMessage = 'No data available';
  @Input() pagination = false;
  @Input() pageSize = 10;
  /** Max page number buttons shown at once (Figma-style window). */
  @Input() maxVisiblePages = 5;

  @ContentChildren(NiTableCellDirective) cellTemplates!: QueryList<NiTableCellDirective<T>>;

  currentPage = 1;
  displayedData: T[] = [];

  ngOnInit(): void {
    this.updateDisplayedData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['pagination'] || changes['pageSize']) {
      this.currentPage = 1;
    }
    this.updateDisplayedData();
  }

  get totalPages(): number {
    if (!this.pagination || this.pageSize <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.data.length / this.pageSize));
  }

  get showPagination(): boolean {
    return this.pagination && this.data.length > 0;
  }

  get visiblePages(): number[] {
    const total = this.totalPages;
    const max = Math.max(1, this.maxVisiblePages);
    if (total <= max) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(1, this.currentPage - Math.floor(max / 2));
    let end = start + max - 1;
    if (end > total) {
      end = total;
      start = Math.max(1, end - max + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getCellTemplate(columnKey: string): NiTableCellDirective<T> | undefined {
    return this.cellTemplates?.find((template) => template.columnKey === columnKey);
  }

  getRowColumnValue(row: T, key: string): unknown {
    return (row as Record<string, unknown>)[key];
  }

  getCellValue(column: NiTableColumn<T>, row: T): string {
    const value = (row as Record<string, unknown>)[column.key];
    if (column.formatter) {
      return column.formatter(value, row);
    }
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return String(value);
  }

  getAlignClass(align: NiTableColumn<T>['align']): string {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  }

  trackByRow = (index: number, row: T): string | number => {
    const key = (row as Record<string, unknown>)[this.trackByKey];
    return key !== undefined && key !== null ? String(key) : index;
  };

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  goToPage(page: number): void {
    const next = Math.min(this.totalPages, Math.max(1, page));
    if (next === this.currentPage) {
      return;
    }
    this.currentPage = next;
    this.updateDisplayedData();
  }

  onPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  onNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  private updateDisplayedData(): void {
    if (!this.pagination) {
      this.displayedData = this.data;
      return;
    }

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    this.displayedData = this.data.slice(start, start + this.pageSize);
  }
}
